import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ManualLoader, Wrapper } from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";
import { StepNavigation } from "../../Account/Register/StepNavigation";
import { RegisterReviewContainer } from "../../Account/Register/Register.style";
import { ApplicationRecord } from "./applicationState";
import {
  AIDE_STEPS,
  AideCareAttestationsData,
  AideCriminalData,
  AideHouseholdData,
  AideIdentityData,
  AideLeasingAckData,
  AideMedicalExpenseData,
  AideRentalHistoryData,
  AideStepKey,
  computeAideResume,
} from "./liveInAideState";
import { AideIdentity } from "./steps/aide/AideIdentity";
import { AideRentalHistory } from "./steps/aide/AideRentalHistory";
import { AideHousehold } from "./steps/aide/AideHousehold";
import { AideCareAttestations } from "./steps/aide/AideCareAttestations";
import { AideMedicalExpense } from "./steps/aide/AideMedicalExpense";
import { AideCriminalScreening } from "./steps/aide/AideCriminalScreening";
import { AideStatesLived } from "./steps/aide/AideStatesLived";
import { AideLeasingAck } from "./steps/aide/AideLeasingAck";
import { AideReviewSign } from "./steps/aide/AideReviewSign";
import {
  useGetApplicationsMutation,
  useSaveApplicationDataMutation,
  useSubmitLiveInAideApplicationMutation,
} from "../../../store/services/privateApi";
import { useGetIncomeLimitsQuery } from "../../../store/services/publicApi";
import { useUnsavedAffordableStepNavigation } from "./stepNavigationGuard";

interface PropertyContact {
  phone?: string;
  email?: string;
  name?: string;
}

interface ApplicationRecordWithLease extends ApplicationRecord {
  unit?: string;
  moveInDate?: string;
  property?: { id: string; name?: string };
  lease?: { id: string; isAffordable?: boolean };
}

// The aide sub-flow persists each screen's slice under its AideStepKey on the
// aide prop_application.applicationData JSON, exactly like the co-applicant
// route persists its own steps. Kept as an index signature so the container is
// agnostic to the individual screen shapes (built in Tasks 3-10).
type AideApplicationData = Partial<Record<AideStepKey, unknown>> & {
  _updatedAt?: number;
  language?: string;
};

/**
 * Live-In Aide sub-flow container.
 *
 * Mirrors CoApplicantApplication's WIRING (route params, hydrate-from-invite,
 * per-step persistence, Previous/Next nav, submit hook) but renders the aide's
 * own 9-screen set. The individual screens land in Tasks 3-10; until then each
 * step renders a placeholder keyed by AIDE_STEPS so the route mounts and the
 * stepper navigates. The Review step's Submit is a stub wired to the existing
 * submit mutation (the dedicated aide submit endpoint arrives in Task 12).
 */
export const LiveInAideApplication = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId") || "";
  const propertyId = searchParams.get("propertyId") || "";

  const steps = [
    t("affordable.application.liveInAide.steps.identity"),
    t("affordable.application.liveInAide.steps.rentalHistory"),
    t("affordable.application.liveInAide.steps.household"),
    t("affordable.application.liveInAide.steps.careAttestations"),
    t("affordable.application.liveInAide.steps.medicalExpense"),
    t("affordable.application.liveInAide.steps.criminalScreening"),
    t("affordable.application.liveInAide.steps.statesLived"),
    t("affordable.application.liveInAide.steps.leasingAck"),
    t("affordable.application.liveInAide.steps.review"),
  ];

  const [getApplications, { isLoading: isFetching }] =
    useGetApplicationsMutation();
  const [saveApplicationData, { isLoading: isSaving }] =
    useSaveApplicationDataMutation();
  const [submitLiveInAide, { isLoading: isSubmitting }] =
    useSubmitLiveInAideApplicationMutation();
  const { data: propertyData } = useGetIncomeLimitsQuery(propertyId, {
    skip: !propertyId,
  }) as { data: { property?: PropertyContact } | undefined };

  const [hasHydrated, setHasHydrated] = useState(false);
  const [applicationData, setApplicationData] = useState<AideApplicationData>(
    {}
  );
  const [appRecord, setAppRecord] = useState<ApplicationRecordWithLease | null>(
    null
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const unsavedStepNavigationMessage = t(
    "affordable.application.unsavedStepNavigation"
  ) as string;
  const { navigateBack: handleBack, navigateToStep: handleStepClick } =
    useUnsavedAffordableStepNavigation({
      currentStep,
      completedSteps,
      setCurrentStep,
      setCompletedSteps,
      confirmMessage: unsavedStepNavigationMessage,
    });

  useEffect(() => {
    if (!applicationId) {
      setHasHydrated(true);
      return;
    }
    getApplications({ id: applicationId, propertyId }).then((res) => {
      const list = (res as { data?: ApplicationRecordWithLease[] }).data;
      const record = list?.find((a) => a.id === applicationId);
      const data = (record?.applicationData || {}) as AideApplicationData;
      setApplicationData(data);
      setAppRecord(record || null);
      if (data.language && data.language !== i18n.language) {
        i18n.changeLanguage(data.language);
      }
      const { completedSteps: completed, resumeStep } = computeAideResume(data);
      setCompletedSteps(new Set(completed));
      setCurrentStep(resumeStep);
      if (record?.status === "submitted") setIsSubmitted(true);
      setHasHydrated(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, propertyId]);

  const handleStepSubmit = async (stepIndex: number, stepData: unknown) => {
    const stepKey = AIDE_STEPS[stepIndex];
    const merged: AideApplicationData = {
      ...applicationData,
      [stepKey]: stepData,
      language: i18n.language,
    };
    const res = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as { data?: ApplicationRecord };
    if (res.data) {
      setApplicationData(res.data.applicationData as AideApplicationData);
      setCompletedSteps((prev) => {
        const next = new Set(prev);
        next.add(stepIndex);
        return next;
      });
      if (stepIndex < steps.length - 1) {
        setCurrentStep(stepIndex + 1);
      }
    }
  };

  // Submit the aide sub-flow. All screen slices (identity … leasingAck) are
  // already persisted on applicationData by handleStepSubmit as the aide moved
  // through the flow, so submit just POSTs the collected data to the dedicated
  // aide submit endpoint (Task 12), which flips the aide app to `submitted` and
  // triggers the lease-forms readiness check. On success we show the submitted
  // confirmation view (mirrors the co-applicant submitted flow). Cert SIGNING
  // happens later, in-portal, after staff release — not here.
  const handleSubmitApplication = async () => {
    const merged: AideApplicationData = {
      ...applicationData,
      language: i18n.language,
    };
    const submitRes = (await submitLiveInAide({
      applicationId,
      applicationData: merged,
    })) as { data?: { application?: ApplicationRecord } };
    const updatedApp = submitRes.data?.application;
    if (updatedApp) {
      setApplicationData(updatedApp.applicationData as AideApplicationData);
    }
    // Whether or not the server echoes the updated record, a successful call
    // means the aide sub-flow is submitted — advance to the confirmation view.
    if (submitRes.data) {
      const lastIdx = AIDE_STEPS.length - 1;
      setCompletedSteps((prev) => {
        const next = new Set(prev);
        next.add(lastIdx);
        return next;
      });
      setIsSubmitted(true);
    }
  };

  if (!applicationId || !propertyId) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">
            {t("affordable.application.notFound")}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  if (isFetching || !hasHydrated) {
    return <ManualLoader />;
  }

  // Placeholder per step. The purpose-built aide screens (AideIdentity,
  // AideRentalHistory, …, AideReviewSign) land in Tasks 3-10; each will replace
  // its placeholder here. Keeping a placeholder per AIDE_STEPS entry lets the
  // route mount and the stepper navigate now.
  const renderStep = () => {
    const stepKey = AIDE_STEPS[currentStep];
    const isReview = stepKey === "review";

    // Screen 1 — Confirm identity (Task 3). Persists its slice under the
    // "identity" step key, then advances to the next step.
    if (stepKey === "identity") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-identity">
          <AideIdentity
            defaultValues={
              applicationData.identity as Partial<AideIdentityData> | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 2 — Rental history (Task 4). Persists its slice under the
    // "rentalHistory" step key, then advances to the next step.
    if (stepKey === "rentalHistory") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-rentalHistory">
          <AideRentalHistory
            defaultValues={
              applicationData.rentalHistory as
                | Partial<AideRentalHistoryData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 3 — Household + HUD assistance (Task 5). Persists its slice under
    // the "household" step key, then advances to the next step.
    if (stepKey === "household") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-household">
          <AideHousehold
            defaultValues={
              applicationData.household as
                | Partial<AideHouseholdData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 4 — HUD care attestations (Task 6). Persists its slice under the
    // "careAttestations" step key, then advances to the next step.
    if (stepKey === "careAttestations") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-careAttestations">
          <AideCareAttestations
            defaultValues={
              applicationData.careAttestations as
                | Partial<AideCareAttestationsData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 5 — Medical expense (Task 7). Persists its slice under the
    // "medicalExpense" step key, then advances to the next step.
    if (stepKey === "medicalExpense") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-medicalExpense">
          <AideMedicalExpense
            defaultValues={
              applicationData.medicalExpense as
                | Partial<AideMedicalExpenseData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 6 — Criminal screening (Task 8). SLICE-KEY QUIRK: the step key is
    // "criminalScreening" but the state field is named `criminal`; defaultValues
    // are read from / persisted back under applicationData.criminalScreening.
    if (stepKey === "criminalScreening") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-criminalScreening">
          <AideCriminalScreening
            defaultValues={
              applicationData.criminalScreening as
                | Partial<AideCriminalData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 7 — States lived (Task 9). Stored as a flat string[] of 2-letter
    // USPS codes (not a nested object slice), so defaultValues is the array
    // directly; persisted back under applicationData.statesLived.
    if (stepKey === "statesLived") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-statesLived">
          <AideStatesLived
            defaultValues={applicationData.statesLived as string[] | undefined}
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 8 — Leasing acknowledgements (Task 10). Persists its slice under
    // the "leasingAck" step key, then advances to the review step.
    if (stepKey === "leasingAck") {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-leasingAck">
          <AideLeasingAck
            defaultValues={
              applicationData.leasingAck as
                | Partial<AideLeasingAckData>
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(currentStep, data)}
            isSaving={isSaving}
          />
        </Box>
      );
    }

    // Screen 9 — Review & Submit (Task 10). Renders a read-only summary of every
    // collected slice, then POSTs to the aide submit endpoint. Cert SIGNING
    // happens later, in-portal, after staff release — not on this screen.
    if (isReview) {
      return (
        <Box sx={{ py: 2 }} data-testid="aide-step-review">
          <AideReviewSign
            defaultValues={{
              identity: applicationData.identity as
                | Partial<AideIdentityData>
                | undefined,
              rentalHistory: applicationData.rentalHistory as
                | Partial<AideRentalHistoryData>
                | undefined,
              household: applicationData.household as
                | Partial<AideHouseholdData>
                | undefined,
              careAttestations: applicationData.careAttestations as
                | Partial<AideCareAttestationsData>
                | undefined,
              medicalExpense: applicationData.medicalExpense as
                | Partial<AideMedicalExpenseData>
                | undefined,
              // SLICE-KEY QUIRK: persisted under the "criminalScreening" step
              // key but the state field is named `criminal`.
              criminal: applicationData.criminalScreening as
                | Partial<AideCriminalData>
                | undefined,
              statesLived: applicationData.statesLived as string[] | undefined,
              leasingAck: applicationData.leasingAck as
                | Partial<AideLeasingAckData>
                | undefined,
            }}
            onSubmit={handleSubmitApplication}
            isSaving={isSubmitting || isSaving}
          />
          {currentStep > 0 && (
            <Box sx={{ mt: 1 }}>
              <Button variant="outlined" onClick={handleBack}>
                {t("affordable.application.previous")}
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    // All 9 steps (8 data screens + review) are handled explicitly above, so
    // this is unreachable. Render nothing rather than a placeholder that could
    // silently persist an empty slice and mark the step "complete".
    return null;
  };

  if (isSubmitted) {
    return (
      <WizardShell>
        <RegisterReviewContainer>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 72, mb: 2 }}
            />
            <Typography variant="h3" sx={{ mb: 1 }}>
              {t("affordable.application.reviewSign.success.title")}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 480, mx: "auto" }}
            >
              {t("affordable.application.reviewSign.success.message")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/settings/applications")}
            >
              {t(
                "affordable.application.reviewSign.success.backToApplications"
              )}
            </Button>
          </Box>
        </RegisterReviewContainer>
      </WizardShell>
    );
  }

  const propertyName =
    appRecord?.property?.name || propertyData?.property?.name || "";
  const unit = appRecord?.unit || "";
  const moveIn = appRecord?.moveInDate
    ? moment(appRecord.moveInDate).format("MM/DD/YYYY")
    : "";
  const headerLine = [
    propertyName,
    unit && `${t("affordable.application.coApplicant.header.unit")} ${unit}`,
    moveIn &&
      `${t("affordable.application.coApplicant.header.moveIn")} ${moveIn}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <WizardShell
      stepper={
        <StepNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onStepClick={handleStepClick}
          stepTitles={steps}
          completedSteps={completedSteps}
        />
      }
    >
      <RegisterReviewContainer>
        {headerLine && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {headerLine}
            </Typography>
          </Box>
        )}
        {renderStep()}
      </RegisterReviewContainer>
    </WizardShell>
  );
};
